// The Diamond Sutra — official campaign EPK.
//
// A dedicated, journalist-ready press destination for the August 19, 2026
// album. It extends the existing press system (/press remains the artist EPK)
// rather than competing with it: the canonical Buddha KA Person/Organization
// entities are referenced by @id, never re-minted.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import diamondSutraCover from "@/assets/the-diamond-sutra-cover.png.asset.json";
import buddhaKaImage from "@/assets/buddha-ka-official.png.asset.json";
import { preloadImageLink } from "@/lib/images";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import { track } from "@/lib/analytics";
import { BOILERPLATE, USAGE_TERMS } from "@/lib/press";
import { NEWSROOM_DISCLOSURE } from "@/lib/news";
import {
  DS_ANGLES,
  DS_BIO_75,
  DS_BIO_150,
  DS_BIO_FULL,
  DS_EPK_PATH,
  DS_FACTS,
  DS_HEADLINE,
  DS_KEY_LINKS,
  DS_PRESS_RELEASE,
  DS_RELEASE,
  DS_SLUG,
  DS_SUBHEADLINE,
  DS_SYNOPSIS,
} from "@/lib/press-diamond-sutra";
import {
  PRESS_KIT_ID,
  breadcrumbs,
  itemList,
  ldScript,
  orgRef,
  pageNode,
  personRef,
  releaseId,
} from "@/lib/schema";
import { trackMarketingPressClick } from "@/lib/marketing/analytics";

const EPK_ID = `${SITE_URL}${DS_EPK_PATH}#epk`;
const RELEASE_STATEMENT_ID = `${SITE_URL}${DS_EPK_PATH}#press-release`;
const COVER_URL = absoluteUrl(diamondSutraCover.url);
const PRESS_RELEASE_DATE = "2026-08-09";

export const Route = createFileRoute("/press/the-diamond-sutra/")({
  head: () => {
    const url = `${SITE_URL}${DS_EPK_PATH}`;
    const title = "The Diamond Sutra Press Kit | Buddha KA — Released August 19, 2026";
    const description =
      "Official EPK for The Diamond Sutra by Buddha KA, released August 19, 2026 — press release, album synopsis, fact sheet, all 17 tracks, story angles, approved bios, artwork, and media contact.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index, follow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "Buddha KA · KAfrequency" },
        { property: "og:image", content: COVER_URL },
        { property: "og:image:alt", content: "The Diamond Sutra — official album cover artwork by Buddha KA" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: COVER_URL },
      ],
      links: [
        { rel: "canonical", href: url },
        preloadImageLink(diamondSutraCover.url, "(max-width: 768px) 92vw, 420px"),
      ].filter(Boolean) as Array<Record<string, string>>,
      scripts: [
        ldScript(
          pageNode({
            type: "WebPage",
            id: EPK_ID,
            url,
            name: "The Diamond Sutra — Official Press Kit",
            description,
            image: COVER_URL,
            imageCaption: "The Diamond Sutra — official album cover artwork",
            mainEntity: { "@id": releaseId(DS_SLUG) },
            about: [{ "@id": releaseId(DS_SLUG) }, personRef(), orgRef()],
            extra: {
              isPartOf: { "@id": PRESS_KIT_ID },
              publisher: orgRef(),
              author: personRef(),
              significantLink: [
                `${SITE_URL}/music/${DS_SLUG}`,
                `${SITE_URL}/artist/buddha-ka`,
                `${SITE_URL}/music/discography`,
                `${SITE_URL}/press`,
                `${SITE_URL}/contact`,
              ],
              hasPart: {
                "@type": "CreativeWork",
                "@id": RELEASE_STATEMENT_ID,
                headline: DS_HEADLINE,
                name: DS_HEADLINE,
                datePublished: PRESS_RELEASE_DATE,
                dateModified: PRESS_RELEASE_DATE,
                inLanguage: "en",
                author: personRef(),
                publisher: orgRef(),
                about: { "@id": releaseId(DS_SLUG) },
                url: `${url}#press-release`,
              },
              mainContentOfPage: itemList(
                (DS_RELEASE.tracks ?? []).map((t, i) => ({
                  name: `${i + 1}. ${t.title}`,
                  path: t.slug ? `/music/${DS_SLUG}/song/${t.slug}` : `/music/${DS_SLUG}`,
                })),
              ),
            },
          }),
        ),
        ldScript(
          breadcrumbs([
            ["Press kit", "/press"],
            ["The Diamond Sutra press kit", DS_EPK_PATH],
          ]),
        ),
      ],
    };
  },
  component: DiamondSutraEPK,
});

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          track("press_kit_copy", { asset: label, campaign: DS_SLUG });
          setTimeout(() => setCopied(false), 1800);
        } catch {
          setCopied(false);
        }
      }}
      aria-label={`Copy ${label}`}
      className="inline-flex min-h-11 items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-4 py-2 text-xs text-gold-luminous hover:bg-gold hover:text-obsidian focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
    >
      <span aria-hidden>{copied ? "Copied" : `Copy ${label}`}</span>
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? `${label} copied to clipboard` : ""}
      </span>
    </button>
  );
}

const ASSETS = [
  {
    label: "The Diamond Sutra — official cover artwork",
    url: diamondSutraCover.url,
    alt: "The Diamond Sutra — official album cover artwork by Buddha KA",
    meta: `${diamondSutraCover.content_type} · ${(diamondSutraCover.size / 1024 / 1024).toFixed(2)} MB`,
    credit: "Courtesy of Buddha KA / KAfrequency",
  },
  {
    label: "Buddha KA — official press portrait",
    url: buddhaKaImage.url,
    alt: "Buddha KA — official press portrait",
    meta: `${buddhaKaImage.content_type} · ${(buddhaKaImage.size / 1024 / 1024).toFixed(2)} MB`,
    credit: "Courtesy of Buddha KA / KAfrequency",
  },
];

function DiamondSutraEPK() {
  const tracks = DS_RELEASE.tracks ?? [];

  useEffect(() => {
    track("press_kit_view", { surface: "epk", campaign: DS_SLUG });
  }, []);

  return (
    <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-24">
      <nav aria-label="Breadcrumb" className="mb-8 text-xs uppercase tracking-[0.25em] text-muted-foreground">
        <Link to="/press" className="hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold">
          Press kit
        </Link>{" "}
        <span aria-hidden>/</span> <span className="text-gold">The Diamond Sutra</span>
      </nav>

      {/* 1. HERO */}
      <header className="grid gap-8 md:grid-cols-[minmax(0,340px)_1fr] md:items-center">
        <ResponsiveImage
          src={diamondSutraCover.url}
          alt="The Diamond Sutra — official album cover artwork by Buddha KA"
          priority
          sizes="(max-width: 768px) 92vw, 340px"
          className="aspect-square w-full rounded-2xl border border-gold/40 bg-charcoal object-cover shadow-halo"
        />
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Official press kit</p>
          <h1 className="mt-3 font-serif text-4xl leading-tight sm:text-5xl md:text-6xl">
            Buddha KA — The Diamond Sutra
          </h1>
          <p className="mt-4 text-lg text-ivory/85">
            17-track album · Released <time dateTime="2026-08-19">August 19, 2026</time> · Out now
          </p>
          <p className="mt-3 max-w-xl text-sm text-muted-foreground">
            Chicago-born independent artist, author, and founder of KAfrequency. Everything on this page is
            first-party and approved for editorial use.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/music/$slug"
              params={{ slug: DS_SLUG }}
              className="inline-flex min-h-11 items-center rounded-full border border-gold px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian"
            >
              View album
            </Link>
            <Link
              to="/artist/buddha-ka"
              className="inline-flex min-h-11 items-center rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold hover:text-gold"
            >
              Official artist page
            </Link>
            <a
              href="#media-assets"
              className="inline-flex min-h-11 items-center rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold hover:text-gold"
            >
              Media assets
            </a>
          </div>
        </div>
      </header>

      {/* 2. PRESS RELEASE */}
      <section id="press-release" className="mt-16 scroll-mt-24" aria-labelledby="press-release-h">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">For immediate release</p>
          <CopyButton text={[DS_HEADLINE, DS_SUBHEADLINE, ...DS_PRESS_RELEASE, BOILERPLATE].join("\n\n")} label="press release" />
        </div>
        <h2 id="press-release-h" className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">
          {DS_HEADLINE}
        </h2>
        <p className="mt-3 text-lg text-ivory/80">{DS_SUBHEADLINE}</p>
        <p className="mt-2 text-xs uppercase tracking-[0.25em] text-muted-foreground">
          <time dateTime={PRESS_RELEASE_DATE}>August 9, 2026</time>
        </p>
        <div className="mt-6 space-y-5 rounded-2xl border hairline bg-charcoal p-5 text-ivory/90 sm:p-7">
          {DS_PRESS_RELEASE.map((p, i) => (
            <p key={i} className="leading-relaxed">
              {p}
            </p>
          ))}
          <p className="border-t hairline pt-5 text-sm text-muted-foreground">{BOILERPLATE}</p>
        </div>
      </section>

      {/* 3. SYNOPSIS */}
      <section id="synopsis" className="mt-16 scroll-mt-24" aria-labelledby="synopsis-h">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 id="synopsis-h" className="font-serif text-3xl">
            Album synopsis
          </h2>
          <CopyButton text={DS_SYNOPSIS.join("\n\n")} label="synopsis" />
        </div>
        <div className="mt-4 space-y-4 rounded-2xl border hairline bg-charcoal p-5 text-ivory/90 sm:p-7">
          {DS_SYNOPSIS.map((p, i) => (
            <p key={i} className="leading-relaxed">
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* 4. FACT SHEET */}
      <section id="fact-sheet" className="mt-16 scroll-mt-24" aria-labelledby="facts-h">
        <h2 id="facts-h" className="font-serif text-3xl">
          Release fact sheet
        </h2>
        <dl className="mt-4 grid gap-3 rounded-2xl border hairline bg-charcoal p-5 sm:grid-cols-2 sm:p-7">
          {DS_FACTS.map((f) => (
            <div key={f.label} className="border-b hairline pb-3 last:border-b-0">
              <dt className="text-[10px] uppercase tracking-[0.25em] text-gold">{f.label}</dt>
              <dd className="mt-1 text-sm text-ivory/90">
                {f.href ? (
                  <Link to={f.href} className="underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous">
                    {f.value}
                  </Link>
                ) : (
                  f.value
                )}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* 5. TRACK LIST */}
      <section id="tracklist" className="mt-16 scroll-mt-24" aria-labelledby="tracks-h">
        <h2 id="tracks-h" className="font-serif text-3xl">
          Full track listing — 17 songs
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Each title links to its own editorial page. No durations, ISRCs, or streaming links are published before
          release.
        </p>
        <ol className="mt-5 grid gap-2 sm:grid-cols-2">
          {tracks.map((t, i) => (
            <li key={t.slug ?? t.title} className="border-b hairline">
              {t.slug ? (
                <Link
                  to="/music/$slug/song/$song"
                  params={{ slug: DS_SLUG, song: t.slug }}
                  className="flex min-h-11 items-center gap-3 py-2 text-sm text-ivory/90 hover:text-gold-luminous focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                >
                  <span className="w-6 shrink-0 text-xs text-muted-foreground">{i + 1}.</span>
                  <span>{t.title}</span>
                </Link>
              ) : (
                <span className="flex min-h-11 items-center gap-3 py-2 text-sm text-ivory/90">
                  <span className="w-6 shrink-0 text-xs text-muted-foreground">{i + 1}.</span>
                  {t.title}
                </span>
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* 6. STORY ANGLES */}
      <section id="story-angles" className="mt-16 scroll-mt-24" aria-labelledby="angles-h">
        <h2 id="angles-h" className="font-serif text-3xl">
          Selected story angles
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Suggested lines of inquiry for interviews and features. These are open hooks, not claims of existing
          coverage.
        </p>
        <ul className="mt-5 grid gap-4 sm:grid-cols-2">
          {DS_ANGLES.map((a) => (
            <li key={a.title} className="rounded-2xl border hairline bg-charcoal p-5">
              <h3 className="font-serif text-lg text-ivory">{a.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ivory/85">{a.body}</p>
              {a.songSlug && (
                <Link
                  to="/music/$slug/song/$song"
                  params={{ slug: DS_SLUG, song: a.songSlug }}
                  className="mt-3 inline-flex min-h-11 items-center text-xs text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous"
                >
                  Read the writing behind this song
                </Link>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* 7. BIOS */}
      <section id="bios" className="mt-16 scroll-mt-24" aria-labelledby="bios-h">
        <h2 id="bios-h" className="font-serif text-3xl">
          Approved biographies
        </h2>
        <div className="mt-5 space-y-6">
          <div className="rounded-2xl border hairline bg-charcoal p-5 sm:p-7">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-serif text-xl text-ivory">Short bio · ~75 words</h3>
              <CopyButton text={DS_BIO_75} label="short bio" />
            </div>
            <p className="mt-3 leading-relaxed text-ivory/90">{DS_BIO_75}</p>
          </div>
          <div className="rounded-2xl border hairline bg-charcoal p-5 sm:p-7">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-serif text-xl text-ivory">Medium bio · ~150 words</h3>
              <CopyButton text={DS_BIO_150} label="medium bio" />
            </div>
            <p className="mt-3 leading-relaxed text-ivory/90">{DS_BIO_150}</p>
          </div>
          <div className="rounded-2xl border hairline bg-charcoal p-5 sm:p-7">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-serif text-xl text-ivory">Full bio · ~400 words</h3>
              <CopyButton text={DS_BIO_FULL.join("\n\n")} label="full bio" />
            </div>
            <div className="mt-3 space-y-4 leading-relaxed text-ivory/90">
              {DS_BIO_FULL.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 8. KEY LINKS */}
      <section id="key-links" className="mt-16 scroll-mt-24" aria-labelledby="links-h">
        <h2 id="links-h" className="font-serif text-3xl">
          Key links
        </h2>
        <ul className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {DS_KEY_LINKS.map((l) => (
            <li key={l.label}>
              {l.internal ? (
                <Link
                  to={l.url}
                  className="flex min-h-11 items-center justify-center rounded-full border hairline px-3 py-2 text-center text-xs hover:border-gold hover:text-gold"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    track("press_outbound_click", { platform: l.label, campaign: DS_SLUG });
                    trackMarketingPressClick({ action: "outbound", platform: l.label, surface: "epk" });
                  }}
                  className="flex min-h-11 items-center justify-center rounded-full border border-gold px-3 py-2 text-center text-xs text-gold hover:bg-gold hover:text-obsidian"
                >
                  {l.label} ↗
                </a>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* 9. MEDIA ASSETS */}
      <section id="media-assets" className="mt-16 scroll-mt-24" aria-labelledby="assets-h">
        <h2 id="assets-h" className="font-serif text-3xl">
          Media assets
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Approved, first-party imagery. Download the original files and credit as listed.
        </p>
        <ul className="mt-5 grid gap-4 sm:grid-cols-2">
          {ASSETS.map((a) => (
            <li key={a.url} className="overflow-hidden rounded-2xl border hairline bg-charcoal">
              <img src={a.url} alt={a.alt} loading="lazy" decoding="async" className="aspect-square w-full object-cover" />
              <div className="p-5">
                <p className="font-serif text-sm text-ivory">{a.label}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {a.meta} · {a.credit}
                </p>
                <a
                  href={a.url}
                  download
                  onClick={() => track("press_kit_asset_download", { asset: a.label, campaign: DS_SLUG })}
                  className="mt-3 inline-flex min-h-11 items-center rounded-full border border-gold/50 bg-gold/10 px-4 py-2 text-xs text-gold-luminous hover:bg-gold hover:text-obsidian focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                >
                  Download original
                </a>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <a
            href="/press/kit.json"
            className="inline-flex min-h-11 items-center rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold"
          >
            Machine-readable EPK (JSON)
          </a>
          <a
            href="/press/releases.xml"
            className="inline-flex min-h-11 items-center rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold"
          >
            Press release RSS
          </a>
          <Link
            to="/press"
            className="inline-flex min-h-11 items-center rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold"
          >
            Full Buddha KA press kit
          </Link>
        </div>
      </section>

      {/* 13. PRESS CONTACT */}
      <section id="press-contact" className="mt-16 scroll-mt-24" aria-labelledby="contact-h">
        <h2 id="contact-h" className="font-serif text-3xl">
          Press &amp; media inquiries
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Interview requests, review copies, podcast bookings, radio, playlist, and partnership inquiries are handled
          through the official contact route. Mark the message “Press / Media Inquiry”.
        </p>
        <Link
          to="/contact"
          className="mt-5 inline-flex min-h-11 items-center rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold"
        >
          Contact — press / media inquiry
        </Link>
      </section>

      <section className="mt-16 rounded-2xl border hairline bg-obsidian/60 p-6 text-sm text-muted-foreground">
        <h2 className="font-serif text-xl text-ivory">Asset usage</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {USAGE_TERMS.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        <p className="mt-3">{NEWSROOM_DISCLOSURE}</p>
      </section>
    </div>
  );
}
